import { useLocalStorage } from '@vueuse/core';
import { inject, provide, watch } from 'vue-demi';
import { devProxy } from '@/utils/helpers/devProxy';
import { useFeatures } from './useFeatures';
import { useAppShell } from './useAppShell';
import { useLightspeedBridge } from './useLightspeedBridge';

const symbol = Symbol('useTeamworkVersion');

function TeamworkVersion() {
  const teamworkVersion = useLocalStorage('teamwork/useTeamworkVersion', '');
  const {
    initialized: featuresInitialized,
    lightspeedEnabled,
    lightspeedToggleEnabled,
  } = useFeatures();
  const { initialized: appShellInitialized, shouldUseAppShell } = useAppShell();
  const { isInLightspeed } = useLightspeedBridge();

  function redirectToLightspeed() {
    // the hash path may also include a querystring
    // in this case swap the "?" for an "&" on the window.location.search string
    const queryString = window.location.hash.includes('?')
      ? window.location.search.replace('?', '&')
      : window.location.search;

    window.location = `${window.location.origin}${window.location.hash.replace(
      /^#\/?/,
      '/app/',
    )}${queryString}`;
  }

  function redirectToTWA() {
    const queryParams = new URLSearchParams(window.top.location.search);

    // Pendo guides can be activated using query strings,
    // Pendo will expect the query string to appear before the hash
    // details: https://support.pendo.io/hc/en-us/articles/360032293371-Understanding-URLs-for-page-tagging#supported-urls-0-1
    const pendoQuery = queryParams.has('pendo')
      ? `?pendo=${queryParams.get('pendo')}`
      : '';
    queryParams.delete('pendo');
    const search = !queryParams.toString() ? '' : `?${queryParams.toString()}`;
    window.top.location.replace(
      `${window.top.location.origin}${window.top.location.pathname.replace(
        /^\/app\/?/,
        `/${pendoQuery}#/`,
      )}${search}${window.top.location.hash}`,
    );
  }

  watch(
    () => {
      if (isInLightspeed.value) {
        return false;
      }
      if (devProxy.isActive) {
        return false;
      }
      if (!featuresInitialized.value) {
        return false;
      }

      if (!appShellInitialized.value) {
        return false;
      }
      if (lightspeedToggleEnabled.value) {
        if (teamworkVersion.value === 'twa') {
          return false;
        }
        if (teamworkVersion.value === 'lightspeed') {
          return true;
        }
      }
      return lightspeedEnabled.value && shouldUseAppShell.value;
    },
    (shouldRedirect) => {
      if (shouldRedirect) {
        redirectToLightspeed();
      }
    },
    { immediate: true },
  );

  return { teamworkVersion, redirectToLightspeed, redirectToTWA };
}

export function provideTeamworkVersion() {
  const teamworkVersion = TeamworkVersion();
  provide(symbol, teamworkVersion);
  return teamworkVersion;
}

export function useTeamworkVersion() {
  return inject(symbol);
}
